<template>
    <div class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-container">
            <!-- Modern Modal Header -->
            <header class="modal-header">
                <div class="header-content">
                    <div class="header-icon">
                        <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" />
                    </div>
                    <div class="header-text">
                        <h2>Upload Media</h2>
                        <p>Add a new video to your media library</p>
                    </div>
                </div>
                <button class="close-btn" @click="$emit('close')" aria-label="Close modal">
                    <font-awesome-icon :icon="['fas', 'times']" />
                </button>
            </header>

            <!-- Upload Steps Indicator -->
            <div class="steps-indicator">
                <div class="step" :class="{ 'active': currentStep >= 1, 'completed': currentStep > 1 }">
                    <div class="step-number">1</div>
                    <span class="step-label">Select Room</span>
                </div>
                <div class="step-divider"></div>
                <div class="step" :class="{ 'active': currentStep >= 2, 'completed': currentStep > 2 }">
                    <div class="step-number">2</div>
                    <span class="step-label">Choose File</span>
                </div>
                <div class="step-divider"></div>
                <div class="step" :class="{ 'active': currentStep >= 3, 'completed': currentStep > 3 }">
                    <div class="step-number">3</div>
                    <span class="step-label">Upload</span>
                </div>
            </div>

            <!-- Form Content -->
            <main class="modal-body">
                <form @submit.prevent="submitForm" class="upload-form">
                    <!-- Step 1: Room Selection -->
                    <div v-if="currentStep === 1" class="form-step">
                        <div class="step-content">
                            <h3>Select Room Type</h3>
                            <p class="step-description">Choose the room type where this video will be displayed</p>
                            
                            <div v-if="objectSpaceData.length > 0" class="room-selection">
                                <div 
                                    v-for="(space, idx) in objectSpaceData" 
                                    :key="idx"
                                    class="room-option"
                                    :class="{ 'selected': form.roomType === space.spaceType }"
                                    @click="selectRoom(space)"
                                >
                                    <div class="room-icon">
                                        <font-awesome-icon :icon="['fas', getRoomIcon(space.spaceType)]" />
                                    </div>
                                    <div class="room-info">
                                        <h4>{{ formatRoomType(space.spaceType) }}</h4>
                                        <p>{{ space.objectSpaceId }}</p>
                                    </div>
                                    <div class="room-check">
                                        <font-awesome-icon :icon="['fas', 'check']" />
                                    </div>
                                </div>
                            </div>
                            
                            <!-- No spaces available message -->
                            <div v-else class="no-spaces-message">
                                <div class="no-spaces-icon">
                                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
                                </div>
                                <h4>No Spaces Available</h4>
                                <p>There are no object spaces available for this property. Please create spaces first before uploading videos.</p>
                                <button type="button" class="btn secondary" @click="$emit('close')">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Step 2: File Selection -->
                    <div v-if="currentStep === 2" class="form-step">
                        <div class="step-content">
                            <h3>Choose Video File</h3>
                            <p class="step-description">Select an MP4 video file to upload</p>
                            
                            <div class="file-upload-area" :class="{ 'has-file': videoFile, 'drag-over': isDragOver }" 
                                 @drop="handleDrop" 
                                 @dragover.prevent="isDragOver = true" 
                                 @dragleave="isDragOver = false"
                                 @click="$refs.videoInput?.click()">
                                <div v-if="!videoFile" class="upload-placeholder">
                                    <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="upload-icon" />
                                    <h4>Drop your video here</h4>
                                    <p>or click to browse files</p>
                                    <span class="file-types">Supported: MP4 files only</span>
                                </div>
                                
                                <div v-if="videoFile" class="file-preview">
                                    <div class="file-icon">
                                        <font-awesome-icon :icon="['fas', 'file-video']" />
                                    </div>
                                    <div class="file-details">
                                        <h4>{{ videoFile.name }}</h4>
                                        <p>{{ formatFileSize(videoFile.size) }}</p>
                                    </div>
                                    <button type="button" class="remove-file" @click.stop="removeFile">
                                        <font-awesome-icon :icon="['fas', 'times']" />
                                    </button>
                                </div>
                            </div>
                            
                            <input 
                                type="file" 
                                ref="videoInput" 
                                accept="video/mp4" 
                                @change="handleVideoSelect" 
                                style="display: none;"
                            />
                        </div>
                    </div>

                    <!-- Step 3: Upload Progress -->
                    <div v-if="currentStep === 3" class="form-step">
                        <div class="step-content">
                            <h3>Uploading Video</h3>
                            <p class="step-description">Please wait while your video is being uploaded</p>
                            
                            <div class="upload-progress">
                                <div class="progress-circle">
                                    <svg class="progress-ring" width="120" height="120">
                                        <circle
                                            class="progress-ring-circle-bg"
                                            stroke="#e2e8f0"
                                            stroke-width="8"
                                            fill="transparent"
                                            r="52"
                                            cx="60"
                                            cy="60"
                                        />
                                        <circle
                                            class="progress-ring-circle"
                                            stroke="#5b2e8e"
                                            stroke-width="8"
                                            fill="transparent"
                                            r="52"
                                            cx="60"
                                            cy="60"
                                            :stroke-dasharray="circumference"
                                            :stroke-dashoffset="strokeDashoffset"
                                        />
                                    </svg>
                                    <div class="progress-text">
                                        <span class="progress-percentage">{{ uploadProgress }}%</span>
                                        <span class="progress-label">Uploading</span>
                                    </div>
                                </div>
                                
                                <div class="upload-details">
                                    <div class="detail-item">
                                        <span class="detail-label">File:</span>
                                        <span class="detail-value">{{ videoFile?.name }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <span class="detail-label">Room:</span>
                                        <span class="detail-value">{{ formatRoomType(form.roomType) }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <span class="detail-label">Status:</span>
                                        <span class="detail-value">{{ uploadStatus }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </main>

            <!-- Modal Footer -->
            <footer class="modal-footer">
                <div class="footer-actions">
                    <button 
                        type="button" 
                        class="btn secondary" 
                        @click="$emit('close')"
                        :disabled="isSubmitting"
                    >
                        Cancel
                    </button>
                    
                    <button 
                        v-if="currentStep === 1"
                        type="button"
                        class="btn primary"
                        @click="nextStep"
                        :disabled="!form.roomType"
                    >
                        Next Step
                        <font-awesome-icon :icon="['fas', 'arrow-right']" />
                    </button>
                    
                    <button 
                        v-if="currentStep === 2"
                        type="button"
                        class="btn primary"
                        @click="startUpload"
                        :disabled="!videoFile"
                    >
                        Upload Video
                        <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" />
                    </button>
                    
                    <div v-if="currentStep === 2" class="step-nav">
                        <button type="button" class="btn text" @click="previousStep">
                            <font-awesome-icon :icon="['fas', 'arrow-left']" />
                            Back
                        </button>
                    </div>
                </div>
            </footer>
        </div>

        <div class="toast-container">
            <transition-group name="toast" tag="div">
                <div
                    v-for="toast in toasts"
                    :key="toast.id"
                    class="toast"
                    :class="[`toast-${toast.type}`]">
                    <p class="toast-message">{{ toast.message }}</p>
                    <button class="toast-close" @click="removeToast(toast.id)">x</button>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import apiService from '@/services/api';
import axios from 'axios'; // Keep for direct S3 upload
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
    faCloudUploadAlt, 
    faTimes, 
    faCheck, 
    faArrowRight, 
    faArrowLeft, 
    faFileVideo,
    faBed,
    faUsers,
    faHome,
    faBuilding,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faCloudUploadAlt, faTimes, faCheck, faArrowRight, faArrowLeft, faFileVideo, faBed, faUsers, faHome, faBuilding, faExclamationTriangle);

export default {
    name: 'UploadMediaView',
    components: {
        FontAwesomeIcon
    },
    data() {
        return {
            currentStep: 1,
            isSubmitting: false,
            uploadProgress: 0,
            uploadStatus: 'Preparing upload...',
            adminProfileData: {},
            objectId: this.$route.params.id,
            objectSpaceData: [],
            signedUrl: '',
            processingId: '',
            videoFile: null,
            isDragOver: false,
            form: {
                roomType: '',
                objectSpaceId: ''
            },
            toasts: [],
        };
    },
    computed: {
        circumference() {
            return 2 * Math.PI * 52; // radius = 52
        },
        strokeDashoffset() {
            return this.circumference - (this.uploadProgress / 100) * this.circumference;
        }
    },
    mounted() {
        let adminProfile = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = JSON.parse(adminProfile);
        let objectSpace = localStorage.getItem(`OBJECTSPACE-${this.objectId}`);
        
        // Handle case when objectSpace data is null or doesn't exist
        if (objectSpace) {
            try {
                let parsedObjectSpace = JSON.parse(objectSpace);
                this.objectSpaceData = parsedObjectSpace?.data || [];
            } catch (error) {
                console.error('Error parsing objectSpace data:', error);
                this.objectSpaceData = [];
            }
        } else {
            this.objectSpaceData = [];
        }
    },
    methods: {
        // Step Navigation
        nextStep() {
            if (this.currentStep < 3) {
                this.currentStep++;
                if (this.currentStep === 2) {
                    this.fetchSignedUrl();
                }
            }
        },
        
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },

        // Room Selection
        selectRoom(space) {
            this.form.roomType = space.spaceType;
            this.form.objectSpaceId = space.objectSpaceId;
        },

        formatRoomType(spaceType) {
            return spaceType ? spaceType.replace('S', ' Sharing') : '';
        },

        getRoomIcon(spaceType) {
            const iconMap = {
                '1S': 'bed',
                '2S': 'users',
                '3S': 'users',
                '4S': 'building',
                'PRIVATE': 'home'
            };
            return iconMap[spaceType] || 'home';
        },

        // File Handling
        handleVideoSelect(event) {
            const file = event.target.files[0];
            this.processFile(file);
        },

        handleDrop(event) {
            event.preventDefault();
            this.isDragOver = false;
            const file = event.dataTransfer.files[0];
            this.processFile(file);
        },

        processFile(file) {
            if (!file) return;
            
            if (file.type === "video/mp4") {
                this.videoFile = file;
            } else {
                this.showError("Please select an MP4 video file.");
                if (this.$refs.videoInput) {
                    this.$refs.videoInput.value = "";
                }
            }
        },

        removeFile() {
            this.videoFile = null;
            if (this.$refs.videoInput) {
                this.$refs.videoInput.value = "";
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // Upload Process
        async fetchSignedUrl() {
            if (!this.form.objectSpaceId) return;

            try {
                const response = await apiService.getUploadVideoApi({
                    objectSpaceId: this.form.objectSpaceId,
                    action: 'URL'
                });
                
                this.signedUrl = response.data.success;
                this.processingId = response.data.processingId;
                
            } catch (error) {
                console.error("Error fetching signed URL:", error);
                this.handleApiError(error, "Failed to prepare upload. Please try again.");
            }
        },

        async startUpload() {
            if (!this.signedUrl || !this.videoFile) {
                this.showError("Please select a video file.");
                return;
            }

            this.currentStep = 3;
            this.isSubmitting = true;
            this.uploadProgress = 0;
            this.uploadStatus = 'Starting upload...';

            try {
                this.uploadStatus = 'Uploading to cloud...';
                
                await axios.put(this.signedUrl, this.videoFile, {
                    headers: { 'Content-Type': 'video/mp4' },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.lengthComputable) {
                            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            
                            if (this.uploadProgress < 30) {
                                this.uploadStatus = 'Uploading to cloud...';
                            } else if (this.uploadProgress < 70) {
                                this.uploadStatus = 'Processing video...';
                            } else if (this.uploadProgress < 100) {
                                this.uploadStatus = 'Finalizing upload...';
                            }
                        }
                    }
                });

                this.uploadStatus = 'Completing upload...';
                await this.callUploadCallback('COMPLETED');
                
                this.uploadStatus = 'Upload completed successfully!';
                
                // Let parent know upload was successful
                this.$emit('success', {
                    objectSpaceId: this.form.objectSpaceId,
                    processingId: this.processingId
                });

                // Close modal after a brief delay to show success
                setTimeout(() => {
                    this.$emit('close');
                }, 1500);

            } catch (err) {
                console.error("Upload failed", err);
                this.uploadStatus = 'Upload failed';
                await this.callUploadCallback('FAILED');
                this.showError("Failed to upload video. Please try again.");
            } finally {
                this.isSubmitting = false;
            }
        },

        async callUploadCallback(status) {
            try {
                await apiService.getUploadVideoApi({
                    objectSpaceId: this.form.objectSpaceId,
                    action: 'CALLBACK',
                    processingId: this.processingId,
                    s3status: status
                });
            } catch (error) {
                console.error(`Callback API failed (${status}):`, error);
                this.handleApiError(error, `Failed to update upload status: ${status}`);
            }
        },

        // Helper method to get auth token
        getAuthToken() {
            const token = readSessionToken();
            if (!token) {
                throw new Error('Authentication token not found');
            }
            return token;
        },

        // Helper method to handle API errors
        handleApiError(error, defaultMessage) {
            // Skip error handling for "no data" cases (status 400 with errorcode 2003)
            if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
                return;
            }

            let message = null;

            if (error?.response?.data) {
                const errorData = error.response.data;
                const status = error.response.status;
                
                // For HTTP 400 errors, always use the reason field from API response
                if (status === 400) {
                    if (errorData.reason) {
                        message = errorData.reason;
                    }
                    // If no reason provided for 400 error, don't show error (no generic messages)
                } else {
                    // For other status codes, prioritize reason field
                    if (errorData.reason) {
                        message = errorData.reason;
                    } else if (errorData.message) {
                        message = errorData.message;
                    } else {
                        // Fallback messages for other status codes only if no reason/message
                        if (status === 401) {
                            message = 'Session expired. Please login again.';
                        } else if (status === 413) {
                            message = 'File size too large. Please select a smaller file.';
                        } else if (status === 429) {
                            message = 'Too many requests. Please wait a moment and try again.';
                        } else if (defaultMessage) {
                            message = defaultMessage;
                        }
                    }
                }
            } else if (defaultMessage) {
                message = defaultMessage;
            }

            // Only show error if we have a message (genuine errors only)
            if (message) {
                this.showError(message);
            }
        },

        // Enhanced error display method
        showError(message) {
            console.error('Upload Error:', message);
            this.showToast(message, 'error');
        },
        showToast(message, type = 'error', duration = 5000) {
            const id = Date.now() + Math.random();
            this.toasts.push({ id, message, type });
            if (duration > 0) {
                setTimeout(() => this.removeToast(id), duration);
            }
        },
        removeToast(id) {
            this.toasts = this.toasts.filter(toast => toast.id !== id);
        },

        // Legacy method for backward compatibility
        async submitForm() {
            this.startUpload();
        }
    }
};
</script>


<style scoped>
/* Modal Overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Modal Header */
.modal-header {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: white;
    padding: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.header-icon {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.header-text {
    text-align: left;
}

.header-text h2 {
    margin: 0 0 4px 0;
    font-size: 24px;
    font-weight: 700;
    text-align: left;
}

.header-text p {
    margin: 0;
    font-size: 14px;
    opacity: 0.9;
    text-align: left;
}

.close-btn {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 8px;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    transition: all 0.2s ease;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

/* Steps Indicator */
.steps-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    position: relative;
}

.step-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.step.active .step-number {
    background: #5b2e8e;
    color: white;
}

.step.completed .step-number {
    background: #10b981;
    color: white;
}

.step-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    text-align: center;
}

.step.active .step-label,
.step.completed .step-label {
    color: #1e293b;
    font-weight: 600;
}

.step-divider {
    width: 60px;
    height: 2px;
    background: #e2e8f0;
    margin: 0 16px;
    position: relative;
    top: -10px;
}

/* Modal Body */
.modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 32px;
}

.form-step {
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.step-content {
    width: 100%;
    max-width: 400px;
    text-align: left;
}

.step-content h3 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px 0;
    text-align: left;
}

.step-description {
    font-size: 16px;
    color: #64748b;
    margin: 0 0 32px 0;
    line-height: 1.5;
    text-align: left;
}

/* Room Selection */
.room-selection {
    display: grid;
    gap: 12px;
    text-align: left;
}

/* No Spaces Message */
.no-spaces-message {
    text-align: center;
    padding: 32px 16px;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 12px;
}

.no-spaces-icon {
    width: 64px;
    height: 64px;
    background: #f59e0b;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px auto;
    font-size: 24px;
    color: white;
}

.no-spaces-message h4 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #92400e;
}

.no-spaces-message p {
    margin: 0 0 24px 0;
    font-size: 14px;
    color: #a16207;
    line-height: 1.5;
}

.room-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;
}

.room-option:hover {
    border-color: #5b2e8e;
    background: #faf7ff;
}

.room-option.selected {
    border-color: #5b2e8e;
    background: #faf7ff;
}

.room-icon {
    width: 48px;
    height: 48px;
    background: #f1f5f9;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #5b2e8e;
    transition: all 0.2s ease;
}

.room-option.selected .room-icon {
    background: #5b2e8e;
    color: white;
}

.room-info {
    flex: 1;
}

.room-info h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
}

.room-info p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    font-family: 'SF Mono', Monaco, monospace;
}

.room-check {
    width: 24px;
    height: 24px;
    border: 2px solid #e2e8f0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    transition: all 0.2s ease;
}

.room-option.selected .room-check {
    background: #5b2e8e;
    border-color: #5b2e8e;
    color: white;
}

/* File Upload Area */
.file-upload-area {
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    padding: 48px 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #f8fafc;
}

.file-upload-area:hover,
.file-upload-area.drag-over {
    border-color: #5b2e8e;
    background: #faf7ff;
}

.upload-placeholder h4 {
    margin: 16px 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
}

.upload-placeholder p {
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #64748b;
}

.upload-icon {
    font-size: 48px;
    color: #cbd5e1;
}

.file-types {
    font-size: 12px;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 4px 12px;
    border-radius: 16px;
    display: inline-block;
}

.file-preview {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    text-align: left;
}

.file-icon {
    width: 48px;
    height: 48px;
    background: #ede9fe;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #5b2e8e;
}

.file-details {
    flex: 1;
}

.file-details h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
}

.file-details p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
}

.remove-file {
    width: 32px;
    height: 32px;
    background: #fee2e2;
    border: none;
    border-radius: 8px;
    color: #dc2626;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.remove-file:hover {
    background: #fecaca;
    transform: scale(1.05);
}

/* Upload Progress */
.upload-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
}

.progress-circle {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.progress-ring {
    transform: rotate(-90deg);
}

.progress-ring-circle {
    transition: stroke-dashoffset 0.5s ease;
}

.progress-text {
    position: absolute;
    text-align: center;
}

.progress-percentage {
    display: block;
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
}

.progress-label {
    display: block;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 4px;
}

.upload-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 300px;
}

.detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f1f5f9;
}

.detail-item:last-child {
    border-bottom: none;
}

.detail-label {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.detail-value {
    font-size: 14px;
    color: #1e293b;
    font-weight: 600;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Modal Footer */
.modal-footer {
    padding: 24px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
}

.footer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.step-nav {
    display: flex;
    align-items: center;
}

/* Buttons */
.btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    text-decoration: none;
    white-space: nowrap;
}

.btn.primary {
    background: #5b2e8e;
    color: white;
}

.btn.primary:hover:not(:disabled) {
    background: #7c3aed;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.btn.secondary {
    background: white;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.btn.secondary:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn.text {
    background: none;
    color: #64748b;
    padding: 8px 12px;
}

.btn.text:hover:not(:disabled) {
    color: #5b2e8e;
    background: rgba(91, 46, 142, 0.1);
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
}

/* Responsive Design */
@media (max-width: 768px) {
    .modal-overlay {
        padding: 12px;
    }
    
    .modal-container {
        max-width: none;
        border-radius: 12px;
    }
    
    .modal-header {
        padding: 20px;
    }
    
    .header-text h2 {
        font-size: 20px;
    }
    
    .steps-indicator {
        padding: 20px 16px;
    }
    
    .step-divider {
        width: 40px;
        margin: 0 8px;
    }
    
    .modal-body {
        padding: 24px 20px;
    }
    
    .step-content {
        max-width: none;
    }
    
    .step-content h3 {
        font-size: 20px;
    }
    
    .room-option {
        padding: 12px;
    }
    
    .file-upload-area {
        padding: 32px 16px;
    }
    
    .upload-icon {
        font-size: 36px;
    }
    
    .modal-footer {
        padding: 20px;
    }
    
    .footer-actions {
        flex-direction: column-reverse;
        align-items: stretch;
    }
    
    .btn {
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .header-content {
        gap: 12px;
    }
    
    .header-icon {
        width: 40px;
        height: 40px;
        font-size: 18px;
    }
    
    .step-content h3 {
        font-size: 18px;
    }
    
    .room-info h4 {
        font-size: 14px;
    }
    
    .file-details h4 {
        font-size: 14px;
    }
}

.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 2000;
    max-width: 380px;
}

.toast {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #fff;
    border-left: 4px solid #ef4444;
    border-radius: 8px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
    padding: 12px 14px;
    margin-bottom: 10px;
}

.toast-error {
    border-left-color: #ef4444;
}

.toast-success {
    border-left-color: #10b981;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    color: #374151;
}

.toast-close {
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateX(20px);
}
</style>